import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

export interface ModernizeConfig {
  hideAllGroups: boolean;     // If true, hides all groups (auto-detects from properties)
  hideAllFields: boolean;     // If true, hides all standalone fields (auto-detects from properties)
  disableAllGroups: boolean;  // If true, disables all groups (auto-detects from properties)
  disableAllFields: boolean;  // If true, disables all standalone fields (auto-detects from properties)
  hiddenFields?: Record<string, string[]>;
  disabledFields?: Record<string, string[]>;
  hiddenGroups?: Record<string, string[]>;
  disabledGroups?: Record<string, string[]>;
}

// Config for LIST pages
export const MODERNIZE_CONFIG_LIST: ModernizeConfig = {
  hideAllGroups: false,      
  hideAllFields: false,
  disableAllGroups: true,   
  disableAllFields: true
};

// Config for DETAIL pages (page interim) and appNavigation interim
// Red = editable, Yellow = readonly (disabled groups), Striked = hidden
export const MODERNIZE_CONFIG_DETAIL: ModernizeConfig = {
  hideAllGroups: false, 
  hideAllFields: false, 
  disableAllGroups: false,
  disableAllFields: false,
  hiddenFields: {
    'page': [],
    'pageview': [],
    'titlebar': [],
    'breadcrumb': [],
    'actionbar': [],
    'button': ['showOn', 'buttonType', 'icon', 'tooltipMessage', 'visibility'],
    'buttongroup': [],
    'workflowactionbar': [],
    'captionbar': [],
    'captionitem': [],
    'form': [],
    'formfield': ['enableLookup'],
    'formsection': [],
    'tabview': [],
    'tab': [],
    'childlist': ['defaultFilter'],
    'standarddatagrid': ['defaultFilter'],
    'menuitem': [],
    'menugroup': []
  },
  disabledFields: {
    'page': ['styleClass'],
    'pageview': ['styleClass'],
    'titlebar': ['styleClass'],
    'breadcrumb': ['styleClass'],
    'actionbar': ['styleClass'],
    'button': ['styleClass'],
    'buttongroup': ['styleClass'],
    'workflowactionbar': ['styleClass'],
    'captionbar': ['styleClass'],
    'captionitem': ['styleClass'],
    'form': ['styleClass'],
    'formfield': ['styleClass'],
    'formsection': ['styleClass'],
    'tabview': ['styleClass'],
    'tab': ['styleClass'],
    'childlist': ['styleClass'],
    'standarddatagrid': ['styleClass'],
    'menuitem': ['onClick', 'styleClass'],
    'menugroup': ['styleClass']
  },
  hiddenGroups: {
    'page': [],
    'pageview': [],
    'titlebar': [],
    'breadcrumb': [],
    'actionbar': [],
    'button': ['Desktop or Mobile', 'Style'],
    'buttongroup': [],
    'workflowactionbar': [],
    'captionbar': [],
    'captionitem': [],
    'form': [],
    'formfield': [],
    'formsection': [],
    'tabview': [],
    'tab': [],
    'childlist': [],
    'standarddatagrid': [],
    'menuitem': [],
    'menugroup': []
  },
  disabledGroups: {
    'page': [],
    'pageview': [],
    'titlebar': [],
    'breadcrumb': [],
    'actionbar': [],
    'button': ['Action'],
    'buttongroup': [],
    'workflowactionbar': [],
    'captionbar': [],
    'captionitem': [],
    'form': ["Access Control"],
    'formfield': ["Access Control"],
    'formsection': ["Access Control"],
    'tabview': ["Access Control"],
    'tab': ["Access Control"],
    'childlist': ["Access Control"],
    'standarddatagrid': ["Access Control"],
    'menuitem': ["Access Control"],
    'menugroup': ["Access Control"]
  }
};

@Injectable({
  providedIn: 'root'
})
export class PropertyPaneService {

  private getFieldsInGroups(groupNames: string[], allProperties: any[]): string[] {

    if (!allProperties || !groupNames.length) return [];
    
    const fieldsInGroups: string[] = [];

    allProperties.forEach(property => {
      if (property?.type === 'group' && groupNames.includes(property.groupName)) {
        property.items?.forEach((item: any) => {
          if (item?.name) {
            fieldsInGroups.push(item.name);
          }
        });
      }
    });

    return fieldsInGroups;

  }

  /**
   * Applies modernize mode restrictions - hides fields, disables fields, hides groups (including all fields inside)
   * @param isListPage - Whether this is a list page (uses MODERNIZE_CONFIG_LIST) or detail page (uses MODERNIZE_CONFIG_DETAIL)
   * @param nodeType - Current node type (e.g., 'button', 'form', 'formfield')
   * @param conditionalCheck - Object controlling field visibility
   * @param conditionCheckGp - Object controlling group visibility
   * @param formGroup - Form group to disable fields on
   * @param allProperties - All properties array (needed to find fields inside hidden groups)
   */
  public applyModernizeRestrictions(
    isListPage: boolean,
    nodeType: string,
    conditionalCheck: any,
    conditionCheckGp: any,
    formGroup: FormGroup | null,
    allProperties: any[] = []
  ): void {
    // Select config based on page type
    const config = isListPage ? MODERNIZE_CONFIG_LIST : MODERNIZE_CONFIG_DETAIL;

    // Auto-detect all groups and fields from properties
    const allGroupNames: string[] = [];
    const allStandaloneFields: string[] = [];
    
    allProperties.forEach(property => {
      if (property?.type === 'group' && property.groupName) {
        allGroupNames.push(property.groupName);
      } else if (property?.name) {
        allStandaloneFields.push(property.name);
      }
    });

    const normalizedNodeType = nodeType?.toLowerCase().replace(/_/g, '') || '';

    this.applyHideRestrictions(config, allGroupNames, allStandaloneFields, allProperties, normalizedNodeType, conditionalCheck, conditionCheckGp);
    this.applyDisableRestrictions(config, allGroupNames, allStandaloneFields, allProperties, normalizedNodeType, formGroup);
  }

  /**
   * Hides groups and fields based on modernize config
   */
  private applyHideRestrictions(
    config: ModernizeConfig,
    allGroupNames: string[],
    allStandaloneFields: string[],
    allProperties: any[],
    normalizedNodeType: string,
    conditionalCheck: any,
    conditionCheckGp: any
  ): void {

    if (config.hideAllGroups || config.hideAllFields) {

      if (config.hideAllGroups) {

        // Hide all groups and their fields
        allGroupNames.forEach(groupName => {
          conditionCheckGp[groupName] = false;
        });

        const fieldsInAllGroups = this.getFieldsInGroups(allGroupNames, allProperties);

        fieldsInAllGroups.forEach(fieldName => {
          conditionalCheck[fieldName] = false;
        });

      }
      
      // Hide all standalone fields
      if (config.hideAllFields) {

        allStandaloneFields.forEach(fieldName => {
          conditionalCheck[fieldName] = false;
        });

      }

    } 
    else {

      const hiddenGroups = config.hiddenGroups?.[normalizedNodeType] || [];
      
      // Find all fields inside hidden groups
      const fieldsInHiddenGroups = this.getFieldsInGroups(hiddenGroups, allProperties);

      // Hide fields (explicit + fields inside hidden groups)
      const hiddenFields = config.hiddenFields?.[normalizedNodeType] || [];
      const allFieldsToHide = [...new Set([...hiddenFields, ...fieldsInHiddenGroups])];

      // Hide fields
      allFieldsToHide.forEach(fieldName => {
        conditionalCheck[fieldName] = false;
      });

      // Hide group headings
      hiddenGroups.forEach(groupName => {
        conditionCheckGp[groupName] = false;
      });

    }

  }

  /**
   * Disables fields and groups based on modernize config
   */
  private applyDisableRestrictions(
    config: ModernizeConfig,
    allGroupNames: string[],
    allStandaloneFields: string[],
    allProperties: any[],
    normalizedNodeType: string,
    formGroup: FormGroup | null
  ): void {

    if (config.disableAllGroups || config.disableAllFields) {

      if (config.disableAllGroups) {

        // Disable all fields inside all groups
        const fieldsInAllGroups = this.getFieldsInGroups(allGroupNames, allProperties);
        fieldsInAllGroups.forEach(fieldName => {
          formGroup?.get(fieldName)?.disable({ onlySelf: true });
        });

      }
      
      if (config.disableAllFields) {

        // Disable all standalone fields
        allStandaloneFields.forEach(fieldName => {
          formGroup?.get(fieldName)?.disable({ onlySelf: true });
        });

      }

    }
    else {

      // Disable specific fields (explicit + fields inside disabled groups)
      const disabledGroups = config.disabledGroups?.[normalizedNodeType] || [];

      const fieldsInDisabledGroups = this.getFieldsInGroups(disabledGroups, allProperties);
      const disabledFields = config.disabledFields?.[normalizedNodeType] || [];

      const allFieldsToDisable = [...new Set([...disabledFields, ...fieldsInDisabledGroups])];

      allFieldsToDisable.forEach(fieldName => {
        formGroup?.get(fieldName)?.disable({ onlySelf: true });
      });

    }
  }
}
